/**
 * MappingStep — match CSV columns to Fias Leads fields.
 *
 * One row per target field with a FieldSourceEditor; required fields
 * are starred and gate the Next button (the wizard reads
 * listMissingRequired). Below, an explicit guardrail panel lists every
 * CSV column that is NOT mapped anywhere — "will not be imported" —
 * so silent data loss is impossible to miss.
 */

import { AlertTriangle } from 'lucide-react';
import {
  listMissingRequired,
  listUnmappedHeaders,
  type ImportFieldDescriptor,
  type ImportMapping,
} from '../../lib/csv-mapping';
import { FieldSourceEditor } from './FieldSourceEditor';

interface Props {
  fields: ReadonlyArray<ImportFieldDescriptor>;
  headers: string[];
  mapping: ImportMapping;
  sampleRow: Record<string, string> | null;
  onSourceChange: (field: string, source: ImportMapping[string]) => void;
}

export function MappingStep({ fields, headers, mapping, sampleRow, onSourceChange }: Props) {
  const unmapped = listUnmappedHeaders(headers, mapping);
  const missingRequired = listMissingRequired(fields, mapping);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Match each Fias Leads field on the left to a column from your file on the right.
      </p>
      <div className="rounded-lg border border-border bg-card divide-y divide-border">
        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-muted/40 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <span>Fias Leads field</span>
          <span>Column from your file</span>
        </div>
        {fields.map((f) => (
          <div key={f.field} className="flex items-center justify-between gap-3 px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm">
                {f.label}
                {f.required && <span className="text-destructive"> *</span>}
              </p>
              {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
            </div>
            <FieldSourceEditor
              fieldLabel={f.label}
              headers={headers}
              source={mapping[f.field] ?? { kind: 'none' }}
              sampleRow={sampleRow}
              onChange={(source) => onSourceChange(f.field, source)}
            />
          </div>
        ))}
      </div>

      {missingRequired.length > 0 && (
        <p className="text-sm text-destructive">
          Map the required field{missingRequired.length === 1 ? '' : 's'}:{' '}
          {missingRequired.map((f) => f.label).join(', ')}
        </p>
      )}

      {unmapped.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
          <p className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4" />
            These columns will not be imported
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{unmapped.join(', ')}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            That's fine if you don't need them — map them above if you do.
          </p>
        </div>
      )}
    </div>
  );
}
